import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { ErrorStateMatcher } from '@angular/material/core'
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field'
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs'
import { map, pairwise, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators'
import { DateService } from '@lemon/core/service/date.service'
import { ProjektitService } from '../../../_jaettu-angular/projektit.service'
import { TositeKortinTiedot, TositeSivu, TositteenLukuTila } from '../lisaa-tositteita.component'
import { annaTositteenTallennusestoObservable, onkoKorjattava, onkoOdottava } from '../tositteen-tallennusesto'

@Component({
  selector: 'app-tosite-kortti',
  templateUrl: './tosite-kortti.component.html',
  styleUrls: ['./tosite-kortti.component.css'],
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeKorttiComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _nykyinenSivuIndeksiSubject = new BehaviorSubject<number>(0)

  @Input() tosite: TositeKortinTiedot
  @Input() jarjestysnumero = 1
  @Input() alvPortaat: number[] | null = null
  @Input() naytaVirheet = false
  @Input() lukittu = false

  @Output() poistaTosite = new EventEmitter<void>()
  @Output() lisaaSivuja = new EventEmitter<File[]>()
  @Output() poistaSivu = new EventEmitter<number>()
  @Output() keskeytaLuku = new EventEmitter<void>()
  @Output() yritaUudelleen = new EventEmitter<void>()
  @Output() ohitaLisays = new EventEmitter<void>()
  @Output() poistaEpaonnistuneet = new EventEmitter<void>()

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>

  naytaProjektitObservable = this._projektitService.naytaProjektitObservable
  naytaModal = false

  virheidenNaytto: ErrorStateMatcher = {
    isErrorState: control => !!control?.invalid && (control.touched || control.dirty),
  }

  get nykyinenSivuIndeksi(): number {
    return this._nykyinenSivuIndeksiSubject.value
  }
  set nykyinenSivuIndeksi(arvo: number) {
    this._nykyinenSivuIndeksiSubject.next(arvo)
  }

  sivutObservable: Observable<TositeSivu[]>
  nykyinenSivuObservable: Observable<TositeSivu | null>
  sivujenMaaraObservable: Observable<number>
  onkoLatausKeskenObservable: Observable<boolean>
  epaonnistuneetIndeksitObservable: Observable<number[]>
  kaikkiLatauksetEpaonnistuivatObservable: Observable<boolean>
  ohitettuObservable: Observable<boolean>
  onkoPurkuvirheObservable: Observable<boolean>
  onkoKorjattavaObservable: Observable<boolean>
  onkoOdottavaObservable: Observable<boolean>
  lukuTilaObservable: Observable<TositteenLukuTila>

  constructor(
    private _projektitService: ProjektitService,
    private _dateService: DateService,
  ) {}

  ngOnInit() {
    this.sivutObservable = this.tosite.sivutSubject.asObservable()

    this.sivujenMaaraObservable = this.sivutObservable.pipe(map(sivut => sivut.length))

    this.nykyinenSivuObservable = combineLatest([this.sivutObservable, this._nykyinenSivuIndeksiSubject]).pipe(
      map(([sivut, indeksi]) => {
        if (sivut.length === 0) {
          return null
        }
        const korjattuIndeksi = Math.min(indeksi, sivut.length - 1)
        return sivut[korjattuIndeksi]
      }),
    )

    this.lukuTilaObservable = this.tosite.lukuTilaSubject.asObservable()

    this.onkoLatausKeskenObservable = this.sivutObservable.pipe(
      switchMap(sivut => {
        if (sivut.length === 0) {
          return of(false)
        }
        return combineLatest(sivut.map(s => s.uploadData.statusObservable)).pipe(map(statukset => statukset.some(s => !s.done)))
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.epaonnistuneetIndeksitObservable = this.sivutObservable.pipe(
      switchMap(sivut => {
        if (sivut.length === 0) {
          return of<number[]>([])
        }
        return combineLatest(sivut.map(s => s.uploadData.statusObservable)).pipe(map(statukset => statukset.flatMap((s, i) => (s.done && s.error ? [i] : []))))
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this._ngUnsubscribe),
    )

    this.kaikkiLatauksetEpaonnistuivatObservable = combineLatest([this.sivutObservable, this.epaonnistuneetIndeksitObservable]).pipe(
      map(([sivut, epaonnistuneet]) => sivut.length > 0 && epaonnistuneet.length === sivut.length),
    )

    this.ohitettuObservable = this.tosite.ohitettuSubject.asObservable()

    this.onkoPurkuvirheObservable = this.sivutObservable.pipe(map(sivut => sivut.some(s => s.purkuvirhe)))

    const tallennusestoObservable = annaTositteenTallennusestoObservable(this.tosite).pipe(shareReplay({ bufferSize: 1, refCount: true }))
    this.onkoKorjattavaObservable = tallennusestoObservable.pipe(map(onkoKorjattava))
    this.onkoOdottavaObservable = tallennusestoObservable.pipe(map(onkoOdottava))

    this.sivutObservable.pipe(startWith([]), pairwise(), takeUntil(this._ngUnsubscribe)).subscribe(([edelliset, nykyiset]) => {
      if (nykyiset.length > edelliset.length) {
        this.nykyinenSivuIndeksi = edelliset.length
      } else if (this.nykyinenSivuIndeksi >= nykyiset.length && nykyiset.length > 0) {
        this.nykyinenSivuIndeksi = nykyiset.length - 1
      }
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  paivitaMaksutapa(maksutapa: number) {
    this.tosite.formGroup.controls.maksutapa.setValue(maksutapa)
    this.tosite.formGroup.controls.maksutapa.markAsTouched()
    this.tosite.formGroup.controls.maksutapa.markAsDirty()
  }

  annaMaksutapaVirhe(): string | null {
    const maksutapa = this.tosite.formGroup.controls.maksutapa
    if (!maksutapa.touched && !maksutapa.dirty) {
      return null
    }
    if (maksutapa.errors?.required) {
      return 'kuitit.muokkaa.validation.maksutapa.required'
    }
    return null
  }

  annaPaivamaaraVirhe(): string | null {
    const paivamaara = this.tosite.formGroup.controls.paivamaara
    if (!paivamaara.touched && !paivamaara.dirty) {
      return null
    }
    const virheet = paivamaara.errors
    if (!virheet) {
      return null
    }
    if (virheet.required) {
      return 'kuitit.muokkaa.validation.pvm.required'
    }
    if (virheet.matDatepickerParse) {
      return 'kuitit.muokkaa.validation.pvm.matDatepickerParse'
    }
    if (virheet.matDatepickerMax) {
      return 'kuitit.muokkaa.validation.pvm.matDatepickerMax'
    }
    if (virheet.matDatepickerMin) {
      return 'kuitit.muokkaa.validation.pvm.matDatepickerMin'
    }
    return null
  }

  annaSummaVirhe(): string | null {
    const summa = this.tosite.formGroup.controls.summa
    if (!summa.touched && !summa.dirty) {
      return null
    }
    const virheet = summa.errors
    if (!virheet) {
      return null
    }
    if (virheet.required) {
      return 'kuitit.muokkaa.validation.summa.required'
    }
    if (virheet.nolla) {
      return 'kuitit.muokkaa.validation.summa.nolla'
    }
    if (virheet.min) {
      return 'kuitit.muokkaa.validation.summa.min'
    }
    if (virheet.max) {
      return 'kuitit.muokkaa.validation.summa.max'
    }
    return null
  }

  onkoHyvitys(): boolean {
    return (this.tosite.formGroup.controls.summa.value ?? 0) < 0
  }

  // Yli 2 kuukautta vanha maksupäivä on todennäköisemmin AI:n tai käyttäjän tulkintavirhe
  onkoVanhaPaiva(): boolean {
    const paivamaara = this.tosite.formGroup.controls.paivamaara.value
    return !!paivamaara && paivamaara < this._dateService.lisaaKuukausia(this._dateService.localDateToDate(this._dateService.currentLocalDate()), -2)
  }

  paivitaToimittajanNimi(nimi: string) {
    this.tosite.formGroup.controls.toimittajanNimi.setValue(nimi)
    this.tosite.formGroup.controls.toimittajanNimi.markAsDirty()
  }

  paivitaToimittajaAvain(avain: string | null) {
    this.tosite.toimittajanTunniste = null
    this.tosite.uusiToimittajaTekoalylta = false
    this.tosite.formGroup.controls.toimittajaAvain.setValue(avain)
    this.tosite.formGroup.controls.toimittajaAvain.markAsTouched()
    this.tosite.formGroup.controls.toimittajaAvain.markAsDirty()
  }

  annaToimittajaVirhe(): string | null {
    const toimittajaAvain = this.tosite.formGroup.controls.toimittajaAvain
    if (!toimittajaAvain.touched && !toimittajaAvain.dirty) {
      return null
    }
    if (this.tosite.formGroup.errors?.toimittajaPuuttuu) {
      return 'kuitit.muokkaa.validation.toimittaja.required'
    }
    return null
  }

  paivitaProjektiAvain(avain: string) {
    this.tosite.formGroup.controls.projektiAvain.setValue(avain)
  }

  edellinenSivu() {
    if (this.nykyinenSivuIndeksi > 0) {
      this.nykyinenSivuIndeksi--
    }
  }

  seuraavaSivu() {
    const maara = this.tosite.sivutSubject.value.length
    if (this.nykyinenSivuIndeksi < maara - 1) {
      this.nykyinenSivuIndeksi++
    }
  }

  poistaSivuKlikattu(sivuIndeksi: number, event: Event) {
    event.stopPropagation()
    if (this.tosite.sivutSubject.value.length <= 1) {
      return
    }
    if (sivuIndeksi <= this.nykyinenSivuIndeksi && this.nykyinenSivuIndeksi > 0) {
      this.nykyinenSivuIndeksi--
    }
    this.poistaSivu.emit(sivuIndeksi)
  }

  avaaTiedostonValinta() {
    this.fileInput?.nativeElement.click()
  }

  tiedostotMuuttui(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files || input.files.length === 0) {
      return
    }
    this.lisaaSivuja.emit(Array.from(input.files))
    input.value = ''
  }

  naytaSuurempana() {
    const sivut = this.tosite.sivutSubject.value
    const nykyinen = sivut[this.nykyinenSivuIndeksi]
    if (nykyinen?.esikatselukuva) {
      this.naytaModal = true
    }
  }

  suljeModal() {
    this.naytaModal = false
  }
}
